import React, { useEffect, useState } from 'react';
import { downloadAuthenticatedFile } from '../../utils/downloadHelper';
import PropTypes from 'prop-types';
import {
    Box,
    Button,
    Card,
    CircularProgress,
    Collapse,
    IconButton,
    Typography,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import {
    AttachFile,
    ExpandLess,
    ExpandMore,
    GetApp,
    Description,
} from '@material-ui/icons';
import Swal from 'sweetalert2';
import { Modal, PrimaryButton, SecondaryButton } from '../../ui';
import { ClientQuoteDetailsApi } from '../../../client/clientQuoteDetails/service/clientQuoteDetailsApi';

const useStyles = makeStyles((theme) => ({
    sectionCard: {
        border: `1px solid #E2E8F0`,
        borderRadius: 12,
        backgroundColor: '#FFFFFF',
        boxShadow: '0 1px 3px rgba(0, 0, 0, 0.08)',
        overflow: 'hidden',
        padding: theme.spacing(2.5),
        marginBottom: theme.spacing(2),
    },
    sectionHeader: {
        cursor: 'pointer',
        padding: 0,
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
    },
    sectionHeaderExpanded: {
        marginBottom: theme.spacing(1.75),
    },
    sectionTitle: {
        margin: 0,
        fontSize: 16,
        fontWeight: 700,
        color: '#0E4A64',
        lineHeight: 1.3,
    },
    sectionBody: {
        padding: 0,
    },
    fileItem: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: theme.spacing(1.2, 1.5),
        marginBottom: theme.spacing(1),
        backgroundColor: '#F8FAFC',
        borderRadius: 10,
        border: '1px solid #E2E8F0',
    },
    fileInfo: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(1),
    },
    fileIcon: {
        color: '#64748B',
    },
    fileActions: {
        display: 'flex',
        gap: theme.spacing(0.5),
    },
    uploadButton: {
        backgroundColor: '#00C2E0',
        color: '#FFFFFF',
        borderRadius: 999,
        textTransform: 'none',
        fontWeight: 600,
        '&:hover': {
            backgroundColor: '#00acc1',
        },
    },
    emptyMessage: {
        color: '#64748B',
        fontStyle: 'italic',
        textAlign: 'center',
        padding: theme.spacing(2),
    },
    inputFile: {
        display: 'none',
    },
    fileLabel: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(1),
        padding: theme.spacing(1.5),
        border: '2px dashed #CBD5E1',
        borderRadius: 8,
        cursor: 'pointer',
        textAlign: 'center',
        '&:hover': {
            borderColor: '#00C2E0',
            backgroundColor: '#F0FAFF',
        },
    },
    modalActions: {
        display: 'flex',
        gap: 12,
        width: '100%',
        justifyContent: 'flex-end',
    },
    loadingButtonContent: {
        display: 'inline-flex',
        alignItems: 'center',
        gap: 8,
    },
    selectedFilesList: {
        marginTop: theme.spacing(2),
        maxHeight: 150,
        overflowY: 'auto',
    },
    selectedFileItem: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        padding: theme.spacing(0.5, 0),
        borderBottom: '1px solid #eee',
    }
}));

const QuoteNfFileSection = ({ quoteId, role, currentLanguage, serviceFiles, status }) => {
    const classes = useStyles();

    const shared = currentLanguage?.bepiService?.shared || {};
    const t = (key, fallback) => {
        const value = shared[key];
        return typeof value === 'string' && value.length > 0 ? value : fallback;
    };

    const isAdmin = role === 'admin';
    const isAguardandoFaturamento = status === 'aguardando faturamento';

    const nfFilesFromApi = Array.isArray(serviceFiles) 
        ? serviceFiles.filter(f => f.type === 'nota_fiscal')
        : [];

    const [expanded, setExpanded] = useState(true);
    const [nfFiles, setNfFiles] = useState(nfFilesFromApi);
    const [modalOpen, setModalOpen] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [selectedFiles, setSelectedFiles] = useState([]);
    const [errorModal, setErrorModal] = useState({
        isOpen: false,
        title: t('shared_details_error_label', 'Erro'),
        message: '',
    });

    useEffect(() => {
        if (Array.isArray(serviceFiles)) {
            const filtered = serviceFiles.filter(f => f.type === 'nota_fiscal');
            setNfFiles(filtered);
        }
    }, [serviceFiles]);

    const toggleSection = () => {
        setExpanded((prev) => !prev);
    };

    const openErrorModal = (message) => {
        setErrorModal({
            isOpen: true,
            title: t('shared_details_error_label', 'Erro'),
            message,
        });
    };

    const closeErrorModal = () => {
        setErrorModal((prev) => ({ ...prev, isOpen: false }));
    };

    const handleOpenModal = () => {
        setSelectedFiles([]);
        setModalOpen(true);
    };

    const handleCloseModal = () => {
        if (uploading) return;
        setModalOpen(false);
        setSelectedFiles([]);
    };

    const handleFileSelect = (event) => {
        const files = Array.from(event.target.files);
        if (files.length > 0) {
            setSelectedFiles((prev) => [...prev, ...files]);
        }
    };

    const handleRemoveSelectedFile = (index) => {
        setSelectedFiles((prev) => prev.filter((_, i) => i !== index));
    };

    const handleUpload = async () => {
        if (selectedFiles.length === 0) {
            openErrorModal(t('shared_details_select_file_warning', 'Selecione pelo menos um arquivo para continuar.'));
            return;
        }

        setUploading(true);

        try {
            const uploadPromises = selectedFiles.map(file => {
                const formData = new FormData();
                formData.append('nf_file', file);
                return ClientQuoteDetailsApi.uploadNfFile(quoteId, formData);
            });

            await Promise.all(uploadPromises);

            handleCloseModal();

            Swal.fire(
                'Sucesso', 
                `${selectedFiles.length} arquivo(s) de nota fiscal anexado(s) com sucesso.`, 
                'success'
            ).then(() => {
                window.location.reload();
            });

        } catch (error) {
            console.error('Erro ao enviar a nota fiscal', error);
            openErrorModal('Não foi possível anexar um ou mais arquivos. Tente novamente.');
        } finally {
            setUploading(false);
        }
    };

    const handleDownload = async (file) => {
        if (file.downloadUrl) {
            try {
                await downloadAuthenticatedFile(file.downloadUrl, file.fileName || file.nome);
            } catch (error) {
                console.error('Falha ao baixar arquivo:', error);
            }
        } else {
            const messageTemplate = t('shared_details_download_info_msg', 'O arquivo #vr0# será baixado.');
            Swal.fire('Info', messageTemplate.replace('#vr0#', file.fileName || file.nome), 'info');
        }
    };

    const formatDate = (dateString) => {
        if (!dateString) return '-';
        const date = new Date(dateString);
        return date.toLocaleDateString('pt-BR', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
        });
    };

    const shouldShowSection = () => {
        if (isAdmin) {
            return isAguardandoFaturamento || nfFiles.length > 0;
        }
        return nfFiles.length > 0;
    };

    if (!shouldShowSection()) return null;

    return (
        <>
            <Card className={classes.sectionCard}>
                <Box
                    className={`${classes.sectionHeader} ${expanded ? classes.sectionHeaderExpanded : ''}`}
                    onClick={toggleSection}
                    onKeyDown={(event) => {
                        if (event.key === 'Enter' || event.key === ' ') {
                            event.preventDefault();
                            toggleSection();
                        }
                    }}
                    role="button"
                    tabIndex={0}
                >
                    <Typography className={classes.sectionTitle}>
                        FATURAMENTO
                    </Typography>
                    <IconButton
                        size="small"
                        onClick={(event) => {
                            event.stopPropagation();
                            toggleSection();
                        }}
                    >
                        {expanded ? <ExpandLess /> : <ExpandMore />}
                    </IconButton>
                </Box>
                <Collapse in={expanded}>
                    <Box className={classes.sectionBody}>
                        {/* 
                         * NOTA FISCAL UPLOAD COMENTADO A PEDIDO 
                         * isAdmin && isAguardandoNf && (
                            <Button
                                variant="contained"
                                className={classes.uploadButton}
                                startIcon={<AttachFile />}
                                onClick={handleOpenModal}
                                style={{ marginBottom: 16 }}
                            >
                                Selecionar arquivos
                            </Button>
                        )*/}
                        
                        {isAdmin && isAguardandoFaturamento && (
                            <Button
                                variant="contained"
                                className={classes.uploadButton}
                                onClick={async () => {
                                    try {
                                        Swal.fire({
                                            title: 'Atenção',
                                            text: 'Deseja marcar o faturamento como enviado e avançar o status para "Pronto para retirada"?',
                                            icon: 'question',
                                            showCancelButton: true,
                                            confirmButtonText: 'Sim',
                                            cancelButtonText: 'Não'
                                        }).then(async (result) => {
                                            if(result.isConfirmed) {
                                                await ClientQuoteDetailsApi.updateQuotationStatus(quoteId, 'pronto para retirada');
                                                Swal.fire('Sucesso', 'Status atualizado com sucesso.', 'success').then(() => {
                                                    window.location.reload();
                                                });
                                            }
                                        });
                                    } catch(e) {
                                        Swal.fire('Erro', 'Não foi possível atualizar o status', 'error');
                                    }
                                }}
                                style={{ marginBottom: 16 }}
                            >
                                Faturamento enviado
                            </Button>
                        )}

                        {nfFiles.length === 0 ? (
                            <Typography className={classes.emptyMessage}>
                                Nenhum arquivo de nota fiscal anexado.
                            </Typography>
                        ) : (
                            nfFiles.map((file) => (
                                <Box key={file.id} className={classes.fileItem}>
                                    <Box className={classes.fileInfo}>
                                        <Description className={classes.fileIcon} />
                                        <Box>
                                            <Typography variant="body1" style={{ fontWeight: 500 }}>
                                                {file.fileName || file.nome}
                                            </Typography>
                                            <Typography variant="caption" color="textSecondary">
                                                Enviado em: {formatDate(file.createdAt || file.data_upload)}
                                            </Typography>
                                        </Box>
                                    </Box>
                                    <Box className={classes.fileActions}>
                                        <IconButton
                                            size="small"
                                            title={t('shared_details_download_tooltip', 'Baixar')}
                                            onClick={() => handleDownload(file)}
                                            style={{ color: '#00C2E0' }}
                                        >
                                            <GetApp />
                                        </IconButton>
                                    </Box>
                                </Box>
                            ))
                        )}
                    </Box>
                </Collapse>
            </Card>

            <Modal
                isOpen={modalOpen}
                onCancel={handleCloseModal}
                showIcon={false}
                align="left"
                maxWidth="560px"
                title="Adicionar Arquivos de Nota Fiscal (PDF)"
                footer={(
                    <div className={classes.modalActions}>
                        <SecondaryButton
                            type="button"
                            onClick={handleCloseModal}
                            disabled={uploading}
                        >
                            {t('shared_details_cancel_btn', 'Cancelar')}
                        </SecondaryButton>
                        <PrimaryButton
                            type="button"
                            onClick={handleUpload}
                            disabled={uploading || selectedFiles.length === 0}
                        >
                            {uploading
                                ? (
                                    <span className={classes.loadingButtonContent}>
                                        <CircularProgress size={18} color="inherit" />
                                        {t('shared_details_uploading_btn', 'Enviando...')}
                                    </span>
                                )
                                : t('shared_details_save_btn', 'Salvar')}
                        </PrimaryButton>
                    </div>
                )}
            >
                <input
                    type="file"
                    id="nf-file-input"
                    accept=".pdf"
                    multiple
                    className={classes.inputFile}
                    onChange={handleFileSelect}
                />
                <label htmlFor="nf-file-input" className={classes.fileLabel}>
                    <AttachFile />
                    <Typography variant="body2">
                        Clique aqui para adicionar arquivos
                    </Typography>
                </label>

                {selectedFiles.length > 0 && (
                    <Box className={classes.selectedFilesList}>
                        <Typography variant="subtitle2" style={{ marginBottom: 8 }}>
                            Arquivos Selecionados:
                        </Typography>
                        {selectedFiles.map((file, index) => (
                            <Box key={index} className={classes.selectedFileItem}>
                                <Typography variant="body2" noWrap style={{ maxWidth: '80%' }}>
                                    {file.name}
                                </Typography>
                                <Typography variant="caption" color="textSecondary">
                                    {(file.size / 1024).toFixed(2)} KB
                                    <Button 
                                        size="small" 
                                        color="secondary" 
                                        onClick={() => handleRemoveSelectedFile(index)}
                                        style={{ minWidth: 'auto', padding: 4, marginLeft: 8 }}
                                    >
                                        Remover
                                    </Button>
                                </Typography>
                            </Box>
                        ))}
                    </Box>
                )}
            </Modal>

            <Modal
                isOpen={errorModal.isOpen}
                type="error"
                title={errorModal.title}
                message={errorModal.message}
                confirmText="OK"
                onConfirm={closeErrorModal}
                onCancel={null}
            />
        </>
    );
};

QuoteNfFileSection.propTypes = {
    quoteId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    role: PropTypes.string,
    currentLanguage: PropTypes.object,
    serviceFiles: PropTypes.array,
    status: PropTypes.string,
};

QuoteNfFileSection.defaultProps = {
    quoteId: null,
    role: 'client',
    currentLanguage: {},
    serviceFiles: [],
    status: '',
};

export default QuoteNfFileSection;
